import { z } from 'zod';

interface UploadFileWithProgressOptions {
  file: Blob;
  fileName: string;
  title: string;
  folder: string;
  apiUrl: string;
  headers?: Record<string, string>;
  onProgress: (percent: number) => void;
}

const uploadResponseSchema = z.object({
  data: z.object({ id: z.string() }).loose(),
});

type UploadResult = z.infer<typeof uploadResponseSchema>['data'];

const parseUploadResponse = (responseText: string): UploadResult | undefined => {
  try {
    return uploadResponseSchema.safeParse(JSON.parse(responseText)).data?.data;
  } catch {
    return undefined;
  }
};

export const uploadFileWithProgress = ({
  file,
  fileName,
  title,
  folder,
  apiUrl,
  headers = {},
  onProgress,
}: UploadFileWithProgressOptions): Promise<UploadResult> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiUrl}/files`);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      const result = xhr.status >= 200 && xhr.status < 300 ? parseUploadResponse(xhr.responseText) : undefined;
      if (result) {
        resolve(result);
      } else {
        reject(new Error('Upload failed'));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    const formData = new FormData();
    formData.append('title', title);
    formData.append('folder', folder);
    formData.append('file', file, fileName);

    xhr.send(formData);
  });
};
